import {
    Box,
    Grid,
    lighten,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import { capitalizeName } from '../../../shared/utils';
import StarRating from './StarRating';
import { getAverageRating, getRatingSummary } from './utils';

interface ReviewsSummaryProps {
    reviews: any[];
    averageRatings: any;
    variant?: 'default' | 'modal';
}

export type RatingDistribution = {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
};

const RatingDistribution: React.FC<{
    reviews: any[];
}> = ({ reviews }) => {
    const ratingArray = getRatingSummary(reviews);
    const theme = useTheme();

    return (
        <Box sx={{ width: '100%', maxWidth: 350, mx: 'auto' }}>
            {ratingArray.map((rating, index) => {
                return (
                    <Box
                        key={index}
                        display='flex'
                        alignItems='center'
                        gap={1}
                        mb={0.5}
                    >
                        <Typography
                            variant='caption'
                            sx={{
                                minWidth: '15px',
                                // color: 'primary.main',
                            }}
                        >
                            {rating.stars}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                                variant='determinate'
                                value={rating.percentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.custom.grey,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: lighten(
                                            theme.palette.secondary.main,
                                            0.3,
                                        ),
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

const ReviewsSummary: React.FC<ReviewsSummaryProps> = ({
    reviews,
    averageRatings,
    variant = 'default',
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const averageRating = getAverageRating(reviews);
    const theme = useTheme();

    return (
        <Box>
            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                mb={4}
            >
                <Typography component={'h2'} variant='visuallyHidden'>
                    Customer ratings
                </Typography>
                <Box display='flex' alignItems='center' gap={2} mb={2}>
                    <Typography
                        component={'span'}
                        variant='h3'
                        sx={{
                            fontSize: isMobile ? '2.5rem' : '3rem',
                        }}
                    >
                        {averageRating}
                    </Typography>
                    <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='flex-start'
                    >
                        <StarRating
                            rating={averageRating}
                            size={isMobile ? 'medium' : 'large'}
                        />

                        <Typography
                            variant='body1'
                            sx={{
                                fontSize: '10px',
                                mt: 0.5,
                            }}
                        >
                            {reviews.length} review
                            {reviews.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Box>

                <RatingDistribution reviews={reviews} />
            </Box>

            <Box>
                {variant === 'modal' ? (
                    <Box
                        flexDirection={{ xs: 'row', lg: 'column' }}
                        sx={{
                            overflowX: { xs: 'auto', lg: 'visible' },
                            pb: { xs: 1, lg: 0 },
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        {Object.entries(averageRatings).map((rating) => (
                            <Box
                                key={rating[0]}
                                display='flex'
                                flexDirection={{
                                    xs: 'column',
                                    lg: 'row',
                                }}
                                justifyContent={{
                                    xs: 'center',
                                    lg: 'space-between',
                                }}
                                alignItems='center'
                                sx={{
                                    p: 2,
                                    border: `solid 1px ${theme.palette.custom.grey}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                        theme.palette.custom.lighterGrey,
                                    minWidth: {
                                        xs: '120px',
                                        lg: 'auto',
                                    },
                                    textAlign: {
                                        xs: 'center',
                                        lg: 'left',
                                    },
                                }}
                            >
                                <Typography
                                    variant='body3'
                                    sx={{
                                        fontWeight: 'bold',
                                        flex: { xs: 'none', lg: 1 },
                                        mb: { xs: 1, lg: 0 },
                                    }}
                                >
                                    {capitalizeName(rating[0])}
                                </Typography>
                                <Box
                                    display='flex'
                                    flexDirection={{
                                        xs: 'column',
                                        lg: 'row',
                                    }}
                                    alignItems='center'
                                    gap={1}
                                >
                                    <Typography
                                        variant='body1'
                                        sx={{
                                            fontSize: {
                                                xs: '14px',
                                                md: '16px',
                                            },
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {Number(rating[1])}
                                    </Typography>
                                    <StarRating
                                        rating={averageRating}
                                        size='small'
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Grid container item justifyContent='center'>
                        <Box
                            component={'dl'}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                overflowX: { xs: 'auto' },
                                pb: { xs: 1 },
                                width: '100%',
                                gap: 2,
                            }}
                        >
                            {Object.entries(averageRatings).map((rating) => (
                                <Box
                                    key={rating[0]}
                                    display='flex'
                                    flexDirection={{
                                        xs: 'column',
                                    }}
                                    justifyContent={{
                                        xs: 'center',
                                    }}
                                    alignItems='center'
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme.palette.custom.grey}`,
                                        borderRadius: 2,
                                        backgroundColor:
                                            theme.palette.custom.lighterGrey,
                                        minWidth: {
                                            xs: '120px',
                                            xxl: '160px',
                                            xxxl: '165px',
                                        },
                                        textAlign: {
                                            xs: 'center',
                                        },
                                    }}
                                >
                                    <Typography
                                        component={'dt'}
                                        variant='body3'
                                        sx={{
                                            fontWeight: 'bold',
                                            flex: { xs: 'none' },
                                            mb: { xs: 1 },
                                        }}
                                    >
                                        {capitalizeName(rating[0])}
                                    </Typography>
                                    <Box
                                        component={'dd'}
                                        display='flex'
                                        flexDirection={{
                                            xs: 'column',
                                        }}
                                        alignItems='center'
                                        gap={1}
                                    >
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontSize: {
                                                    xs: '14px',
                                                    md: '16px',
                                                },
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {Number(rating[1]) || '-'}
                                        </Typography>
                                        {Number(rating[1]) ? (
                                            <StarRating
                                                rating={Number(rating[1])}
                                                size='small'
                                            />
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: '14px',
                                                        md: '16px',
                                                    },
                                                }}
                                            >
                                                no ratings yet
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default ReviewsSummary;
